import React from 'react';
import { motion } from 'motion/react';
import { Send, Clock, Globe, ShieldCheck } from 'lucide-react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';

const PostScheduler = () => {
  return (
    <div className="pt-24 pb-24 bg-[#030303] min-h-screen">
      <SEO 
        title="Social Media Post Scheduler - Automate Your Content"
        description="Schedule your social media posts for the best times with PlataPost's integrated scheduler. Manage Instagram, LinkedIn, and more from one dashboard."
      />

      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center gap-20 mb-40">
          <div className="flex-1 space-y-8">
            <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full glass-card border border-white/5 text-indigo-400 text-[10px] font-black uppercase tracking-widest leading-none">
              <Clock className="w-3.5 h-3.5" />
              <span>Full Automation</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">Social Media <br /> <span className="text-indigo-500">Post Scheduler.</span></h1>
            <p className="text-xl text-gray-400 mb-8 leading-relaxed font-medium">
              Set it and forget it. Our scheduler ensures your content reaches your audience when they're most active, without you having to be at your desk.
            </p>
            <Link to="/dashboard" className="inline-flex px-10 py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
              Start Scheduling
            </Link>
          </div>
          <div className="flex-1 w-full glass-card rounded-[4rem] p-12 relative overflow-hidden border border-white/5 shadow-2xl shadow-indigo-600/5 transition-all hover:border-indigo-500/20 group">
             <div className="absolute inset-0 bg-indigo-600/5 blur-[100px] pointer-events-none" />
             <div className="relative h-64 bg-white/5 rounded-3xl shadow-xl overflow-hidden border border-white/10 p-8 flex items-center justify-center group-hover:scale-105 transition-transform duration-500">
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto border border-indigo-500/20 shadow-xl shadow-indigo-500/10">
                    <Clock className="w-10 h-10 text-indigo-400 animate-pulse" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-2xl font-black text-white">Optimal Time: 10:00 AM</p>
                    <p className="text-gray-400 text-sm font-medium">Targeting Highest Engagement</p>
                  </div>
                </div>
                <div className="absolute top-6 right-6">
                  <div className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 text-[10px] font-black rounded-full uppercase tracking-widest border border-emerald-500/20">Queue Ready</div>
                </div>
             </div>
          </div>
        </div>

        <section className="mb-40">
          <div className="text-center mb-24 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tighter leading-tight">Consistent Presence, Zero Effort</h2>
            <p className="text-gray-400 font-medium max-w-xl mx-auto">Focus on strategy while we handle the distribution.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Globe, title: "Multi-Platform Sync", desc: "Schedule posts for all your accounts in a single session." },
              { icon: Clock, title: "Customizable Queues", desc: "Set your own preferred posting times for each day of the week." },
              { icon: ShieldCheck, title: "Direct Publishing", desc: "Automatic posting for LinkedIn, Facebook, and Instagram Professional accounts." }
            ].map((feature, i) => (
               <div key={i} className="p-10 glass-card rounded-[3rem] border border-white/5 hover:bg-white/5 transition-all group">
                  <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-8 text-indigo-400 group-hover:scale-110 group-hover:rotate-6 transition-all shadow-xl shadow-indigo-600/5">
                    <feature.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-black text-white mb-4 tracking-tight">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed font-medium">{feature.desc}</p>
               </div>
            ))}
          </div>
        </section>

        <div className="text-center glass-card rounded-[4rem] p-24 border border-white/5 relative overflow-hidden shadow-2xl shadow-indigo-600/5">
          <div className="absolute inset-0 bg-indigo-600/10 blur-[150px] pointer-events-none" />
          <h2 className="text-4xl lg:text-6xl font-black mb-10 text-white tracking-tighter leading-tight relative z-10">Reclaim Your <span className="text-indigo-400">Weekends.</span></h2>
          <p className="text-gray-400 text-lg mb-12 max-w-2xl mx-auto font-medium relative z-10 leading-relaxed">
            Plan your entire month's content on a Monday and let PlataPost handle the rest.
          </p>
          <Link to="/dashboard" className="relative z-10 inline-flex px-12 py-6 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
             Schedule My Calendar
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PostScheduler;
